<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useHead, useRequestURL, useSeoMeta } from '#imports'
import { useBlogContent } from '../../../composables/useBlogContent'
import { useRelatedArticles } from '../../../composables/useRelatedArticles'
import { useBlogTelemetry } from '../../../composables/useBlogTelemetry'
import { useAieoValidation } from '../../../composables/useAieoValidation'
import {
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildFaqJsonLd,
  createJsonLdScript,
  toAbsoluteUrl,
} from '../../../composables/useStructuredData'
import { normalizeCanonicalUrl } from '~~/utils/canonical'
import RelatedArticles from '../../components/blog/RelatedArticles.vue'
import AieoValidationDebugPanel from '../../../components/blog/AieoValidationDebugPanel.vue'
import type { BlogArticle, BlogFaqItem } from '~/types/blog'
import type { AieoValidationResult } from '../../../composables/useAieoValidation'

interface BlogPageAuthor {
  name?: string
  title?: string
  expertise?: string
  bio?: string
}

interface BlogPageDocument {
  path: string
  body?: unknown
  title?: string
  author?: string | BlogPageAuthor
  hero_image?: string
  hero_image_alt?: string
  excerpt?: string
  description?: string
  faq?: Array<{ question: string; answer: string }>
  focus_question?: string
  focusQuestion?: string
  evidence_sources?: Array<string | { url: string; label?: string; note?: string }>
  evidenceSources?: Array<string | { url: string; label?: string; note?: string }>
  meta?: {
    seoTitle?: string
    seoDescription?: string
    description?: string
    excerpt?: string
    ogImage?: string
    hero_image?: string
  } & Record<string, unknown>
}

const route = useRoute()
const requestURL = useRequestURL()
const { emitSchemaValidationPassed, emitSchemaValidationFailed, emitMetaValidationPassed, emitMetaValidationFailed } = useAnalytics()
const { getArticleBySlug } = useBlogContent()
const { getRelatedArticles } = useRelatedArticles()
const { trackBlogArticleViewed, startBlogEngagementTracking } = useBlogTelemetry()
const { validateArticle, formatValidationForLog } = useAieoValidation()
const RELATED_ARTICLES_MAX = 3
const FALLBACK_AUTHOR_NAME = 'QxAIOS Team'

// AIEO Validation state
const aieoValidationResult = ref<AieoValidationResult | null>(null)
const showAieoDebugPanel = ref(false)

const slug = computed(() =>
  typeof route.params.slug === 'string'
    ? route.params.slug.trim()
    : '',
)

if (!slug.value) {
  throw createError({ statusCode: 404, statusMessage: 'Article not found', fatal: true })
}

const { data: article } = await useAsyncData<BlogArticle | null>(
  `blog-article-${slug.value}`,
  () => getArticleBySlug(slug.value),
)

if (!article.value) {
  throw createError({ statusCode: 404, statusMessage: 'Article not found', fatal: true })
}

const { data: articleDocument } = await useAsyncData<BlogPageDocument | null>(
  `blog-article-doc-${slug.value}`,
  () => queryCollection('blog').path(article.value?.path ?? `/blog/${slug.value}`).first(),
)

if (!articleDocument.value) {
  throw createError({ statusCode: 404, statusMessage: 'Article not found', fatal: true })
}

const { data: relatedArticles } = await useAsyncData<BlogArticle[]>(
  `blog-related-${slug.value}`,
  () =>
    article.value
      ? getRelatedArticles(article.value, { maxResults: RELATED_ARTICLES_MAX })
      : Promise.resolve([]),
)

function formatDate(isoDate: string | null): string {
  if (!isoDate) return ''
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

const publishDate = computed(() => formatDate(article.value?.publishedAt ?? null))
const lastUpdatedDate = computed(() => {
  const publishedAt = article.value?.publishedAt
  const lastUpdated = article.value?.lastUpdated
  if (!publishedAt || !lastUpdated) return ''
  if (new Date(lastUpdated).getTime() <= new Date(publishedAt).getTime()) return ''
  return formatDate(lastUpdated)
})

const authorDetails = computed(() => {
  const fallbackName = article.value?.author.name?.trim() || FALLBACK_AUTHOR_NAME
  const source = articleDocument.value?.author

  if (typeof source === 'string') {
    return { name: source.trim() || fallbackName, title: null, bio: null }
  }

  if (source && typeof source === 'object') {
    return {
      name: source.name?.trim() || fallbackName,
      title: source.title?.trim() || source.expertise?.trim() || null,
      bio: source.bio?.trim() || null,
    }
  }

  return { name: fallbackName, title: null, bio: null }
})

const heroImage = computed(() => article.value?.heroImage ?? articleDocument.value?.hero_image ?? null)
const heroAlt = computed(() =>
  articleDocument.value?.hero_image_alt?.trim() || `Hero image for ${article.value?.title ?? 'article'}`,
)

const canonicalPath = computed(() => article.value?.path ?? route.path)
const siteOrigin = computed(() => {
  if (import.meta.client && globalThis.location?.origin) {
    return globalThis.location.origin
  }
  return requestURL.origin
})
const canonicalUrl = computed(() => normalizeCanonicalUrl(`${siteOrigin.value}${canonicalPath.value}`))

const articleUrl = computed(() => canonicalUrl.value)
const metadataDescription = computed(() =>
  articleDocument.value?.meta?.seoDescription?.trim() ||
  articleDocument.value?.meta?.description?.trim() ||
  articleDocument.value?.description?.trim() ||
  article.value?.description?.trim() ||
  articleDocument.value?.meta?.excerpt?.trim() ||
  articleDocument.value?.excerpt?.trim() ||
  article.value?.excerpt?.trim() ||
  'Read this article on the QxAIOS blog.',
)
const metadataTitle = computed(() =>
  articleDocument.value?.meta?.seoTitle?.trim() ||
  `${article.value?.title ?? 'Blog article'} | QxAIOS Blog`,
)
const metadataImage = computed(() =>
  toAbsoluteUrl(siteOrigin.value, articleDocument.value?.meta?.ogImage ?? null) ||
  toAbsoluteUrl(siteOrigin.value, heroImage.value),
)

const structuredDataImage = computed(() => metadataImage.value || `${siteOrigin.value}/images/logo.svg`)

interface BlogContentNode {
  tag?: string
  children?: unknown[]
  value?: unknown
}

function getNodeText(value: unknown): string {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map(getNodeText).join(' ')
  if (!value || typeof value !== 'object') return ''

  const record = value as BlogContentNode
  if (typeof record.value === 'string') return record.value

  return record.children ? record.children.map(getNodeText).join(' ') : ''
}

function normalizeText(value: unknown): string {
  return getNodeText(value).replace(/\s+/g, ' ').trim()
}

function isHeadingNode(node: unknown, tagName: string): node is BlogContentNode {
  return !!node && typeof node === 'object' && (node as BlogContentNode).tag === tagName
}

function extractFaqFromContent(body: unknown): BlogFaqItem[] {
  const children = Array.isArray((body as BlogContentNode | undefined)?.children)
    ? ((body as BlogContentNode).children ?? [])
    : []

  const faqHeadingIndex = children.findIndex((node) => {
    if (!isHeadingNode(node, 'h2') && !isHeadingNode(node, 'h3')) return false
    const heading = normalizeText(node.children)
    return heading === 'faq' || heading === 'frequently asked questions'
  })

  if (faqHeadingIndex === -1) return []

  const items: BlogFaqItem[] = []
  let currentQuestion: string | null = null
  let currentAnswerParts: unknown[] = []

  const flushItem = () => {
    const question = currentQuestion?.trim() ?? ''
    const answer = normalizeText(currentAnswerParts)
    if (question && answer) {
      items.push({ question, answer })
    }
    currentQuestion = null
    currentAnswerParts = []
  }

  for (const node of children.slice(faqHeadingIndex + 1)) {
    if (isHeadingNode(node, 'h2')) break

    if (isHeadingNode(node, 'h3') || isHeadingNode(node, 'h4')) {
      flushItem()
      currentQuestion = normalizeText(node.children)
      continue
    }

    if (currentQuestion) {
      currentAnswerParts.push(node)
    }
  }

  flushItem()
  return items
}

const faqItems = computed(() => {
  const items = [
    ...(article.value?.faq ?? []),
    ...extractFaqFromContent(articleDocument.value?.body),
  ]

  const seen = new Set<string>()
  return items.filter((item) => {
    const key = item.question.trim().toLowerCase()
    if (!item.question.trim() || !item.answer.trim() || seen.has(key)) return false
    seen.add(key)
    return true
  })
})

const articleSchema = computed(() =>
  buildArticleJsonLd({
    headline: article.value?.title ?? '',
    description: metadataDescription.value,
    authorName: authorDetails.value.name,
    datePublished: article.value?.publishedAt ?? '',
    dateModified: article.value?.lastUpdated ?? undefined,
    image: structuredDataImage.value,
    url: canonicalUrl.value,
  }),
)

const faqJsonLd = computed(() => buildFaqJsonLd(faqItems.value))
const breadcrumbJsonLd = computed(() =>
  buildBreadcrumbJsonLd([
    { name: 'Home', item: `${siteOrigin.value}/` },
    { name: 'Blog', item: `${siteOrigin.value}/blog` },
    { name: article.value?.title ?? 'Article', item: canonicalUrl.value },
  ]),
)

if (articleSchema.value.schema) {
  emitSchemaValidationPassed('blog', 'article')
} else {
  emitSchemaValidationFailed('blog', 'article', articleSchema.value.reasonCodes)
}

if (faqJsonLd.value) {
  emitSchemaValidationPassed('blog', 'faq')
}

if (breadcrumbJsonLd.value) {
  emitSchemaValidationPassed('blog', 'breadcrumb')
} else {
  emitSchemaValidationFailed('blog', 'breadcrumb', ['missing_breadcrumb_items'])
}

const missingMetadataFields = computed(() => {
  const missing: string[] = []
  if (!metadataTitle.value) missing.push('title')
  if (!metadataDescription.value) missing.push('description')
  if (!canonicalUrl.value) missing.push('canonical_url')
  if (!metadataImage.value) missing.push('social_image')
  return missing
})

if (missingMetadataFields.value.length > 0) {
  emitMetaValidationFailed('blog', missingMetadataFields.value)
} else {
  emitMetaValidationPassed('blog')
}

const jsonLdScripts = computed(() =>
  [articleSchema.value.schema, faqJsonLd.value, breadcrumbJsonLd.value]
    .filter((schema): schema is Record<string, unknown> => schema !== null)
    .map((schema) => createJsonLdScript(schema)),
)

useSeoMeta({
  title: () => metadataTitle.value,
  description: () => metadataDescription.value,
  ogType: 'article',
  ogTitle: () => metadataTitle.value,
  ogDescription: () => metadataDescription.value,
  ogUrl: () => canonicalUrl.value,
  ogImage: () => metadataImage.value,
  ogImageAlt: () => heroAlt.value,
  articlePublishedTime: () => article.value?.publishedAt ?? undefined,
  articleModifiedTime: () => article.value?.lastUpdated ?? undefined,
  twitterCard: () => (metadataImage.value ? 'summary_large_image' : 'summary'),
  twitterTitle: () => metadataTitle.value,
  twitterDescription: () => metadataDescription.value,
  twitterImage: () => metadataImage.value,
})

useHead(() => ({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl.value,
    },
  ],
  script: jsonLdScripts.value,
}))
const encodedArticleUrl = computed(() => encodeURIComponent(articleUrl.value))
const encodedTitle = computed(() => encodeURIComponent(article.value?.title ?? ''))

const socialLinks = computed(() => ({
  twitter: `https://twitter.com/intent/tweet?url=${encodedArticleUrl.value}&text=${encodedTitle.value}`,
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedArticleUrl.value}`,
  linkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedArticleUrl.value}`,
}))

let stopEngagementTracking: (() => void) | null = null

onMounted(() => {
  if (!article.value) return
  trackBlogArticleViewed(
    route.path,
    article.value.slug,
    article.value.title,
    article.value.categories,
    article.value.readingTime,
  )
  stopEngagementTracking = startBlogEngagementTracking({
    pagePath: route.path,
    pageType: 'article',
  })

  // Run AIEO validation
  if (article.value && articleDocument.value) {
    aieoValidationResult.value = validateArticle(article.value, articleDocument.value.body)

    // Log validation results to console for QA/author review
    if (globalThis.console) {
      const formattedLog = formatValidationForLog(aieoValidationResult.value)
      console.log(formattedLog)

      // Also expose via global object for debugging
      if (import.meta.dev) {
        ;(
          globalThis as typeof globalThis & {
            __AIEO_VALIDATION__?: AieoValidationResult | null
          }
        ).__AIEO_VALIDATION__ = aieoValidationResult.value
        console.log(
          '%cAIEO Validation Data: globalThis.__AIEO_VALIDATION__',
          'color: #0066cc; font-weight: bold;',
        )
      }
    }
  }
})

onBeforeUnmount(() => {
  stopEngagementTracking?.()
  stopEngagementTracking = null
})
</script>

<template>
  <article
    v-if="article && articleDocument"
    class="relative mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8"
  >
    <div class="pointer-events-none absolute -left-24 top-10 h-64 w-64 rounded-full bg-spruce-100/60 blur-3xl" />
    <div class="pointer-events-none absolute -right-24 top-28 h-72 w-72 rounded-full bg-sky-100/60 blur-3xl" />

    <header class="relative mb-8 rounded-3xl border border-spruce-100 bg-white/90 p-6 shadow-sm backdrop-blur sm:p-8">
      <p class="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-spruce-700">
        QxAIOS Technical Brief
      </p>
      <h1 class="text-4xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl">
        {{ article.title }}
      </h1>

      <ul
        class="mt-5 flex flex-wrap items-center gap-2 text-sm text-gray-600"
        aria-label="Article metadata"
      >
        <li
          v-if="publishDate"
          class="rounded-full border border-gray-200 bg-gray-50 px-3 py-1"
        >
          Published {{ publishDate }}
        </li>
        <li
          v-if="lastUpdatedDate"
          class="rounded-full border border-gray-200 bg-gray-50 px-3 py-1"
        >
          Updated {{ lastUpdatedDate }}
        </li>
        <li class="rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
          By {{ authorDetails.name }}
        </li>
        <li class="rounded-full border border-spruce-200 bg-spruce-50 px-3 py-1 font-medium text-spruce-800">
          {{ article.readingTime }} min read
        </li>
      </ul>

      <ul
        class="mt-5 flex flex-wrap gap-2"
        aria-label="Article categories and tags"
      >
        <li
          v-for="category in article.categories"
          :key="`cat-${category}`"
          class="rounded-full border border-spruce-200 bg-spruce-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-spruce-700"
        >
          {{ category }}
        </li>
        <li
          v-for="tag in article.tags"
          :key="`tag-${tag}`"
          class="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700"
        >
          #{{ tag }}
        </li>
      </ul>
    </header>

    <img
      v-if="heroImage"
      :src="heroImage"
      :alt="heroAlt"
      class="relative mb-8 w-full rounded-3xl border border-spruce-100 object-cover shadow-md"
      loading="eager"
      fetchpriority="high"
      decoding="async"
    >

    <ContentRenderer
      :value="articleDocument"
      class="prose prose-gray relative max-w-none rounded-3xl border border-spruce-100 bg-white/95 p-6 shadow-sm sm:p-8 prose-headings:font-bold prose-headings:tracking-tight prose-h2:text-spruce-800 prose-h3:text-spruce-700 prose-a:text-spruce-700 prose-a:decoration-spruce-200 hover:prose-a:text-spruce-800"
    />

    <section class="mt-10 rounded-2xl border border-spruce-100 bg-white p-6 shadow-sm">
      <h2 class="text-lg font-semibold text-gray-900">
        Share this article
      </h2>
      <ul class="mt-3 flex flex-wrap gap-3">
        <li>
          <a
            :href="socialLinks.twitter"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700 transition hover:border-spruce-200 hover:bg-spruce-50 hover:text-spruce-800"
            :aria-label="`Share ${article.title} on X`"
          >
            Share on X
          </a>
        </li>
        <li>
          <a
            :href="socialLinks.facebook"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700 transition hover:border-spruce-200 hover:bg-spruce-50 hover:text-spruce-800"
            :aria-label="`Share ${article.title} on Facebook`"
          >
            Share on Facebook
          </a>
        </li>
        <li>
          <a
            :href="socialLinks.linkedIn"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex rounded-full border border-gray-200 px-3 py-1 text-sm font-medium text-gray-700 transition hover:border-spruce-200 hover:bg-spruce-50 hover:text-spruce-800"
            :aria-label="`Share ${article.title} on LinkedIn`"
          >
            Share on LinkedIn
          </a>
        </li>
      </ul>
    </section>

    <section class="mt-10">
      <BlogAuthorBlock
        :name="authorDetails.name"
        :title="authorDetails.title"
        :bio="authorDetails.bio"
      />
    </section>

    <RelatedArticles :articles="relatedArticles ?? []" />

    <!-- AIEO Validation Debug Panel (non-blocking) -->
    <AieoValidationDebugPanel
      :validation-result="aieoValidationResult"
      :visible="showAieoDebugPanel"
    />
  </article>
</template>
